import {FC} from 'react';
import {useParams, useNavigate, useSearchParams} from "react-router-dom";
import {ArrowDown} from "lucide-react";
import DashboardLayout from "@/components/layouts/Dashboard";
import GuidedShredsTour from "@/components/GuidedShredsTour";
import {Alert} from "@/components/ui/Alert/index.ts";
import AIGenerationCard from "@/components/ui/AiGenerationCard";
import ContainerOverlay from "@/components/ui/ContainerOverlay";
import {apiDeleteShred} from "@/services/ShredService";
import {HelpCircle, Eye, EyeOff} from "lucide-react";
import {useSimpleTour} from "@/contexts/SimpleTourProvider";

// Components
import WorkflowGuide from "./components/WorkflowGuide";
import ServiceHeader from "./components/ServiceHeader";
import DataModelStep from "./components/DataModelStep";
import FeatureDevelopmentStep from "./components/FeatureDevelopmentStep";
import ServiceAssemblyStep from "./components/ServiceAssemblyStep";

// Hooks
import {useShredsData} from "./hooks/useShredsData";
import {useTourState} from "./hooks/useTourState";

interface ShredsProps {
}

const Shreds: FC<ShredsProps> = () => {
    const {id, serviceId} = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    searchParams.get("demo");
    const {isActive: isGlobalGuideActive} = useSimpleTour();
    const {
        initialLoading,
        loading,
        serviceStatus,
        shredsArr,
        generateShredModel,
        setGenerateShredModel,
        serviceInfo,
        generatingShredId,
        error,
        setError,
        isWaitingForAPI,
        loadShreds,
        onGenerateShred,
        onGenerateSingleShred,
        setShreds
    } = useShredsData();

    // Derived state
    const dataModelShred = shredsArr.find(shred => shred.model);
    const featureShreds = shredsArr.filter(shred => !shred.model);
    const incompleteShreds = featureShreds.filter(shred => shred.progress < 100);
    const completeShreds = featureShreds.filter(shred => shred.progress >= 100);

    // Updated model ready check to include validation
    const isModelReady = dataModelShred ?
        ['SPECS_GENERATED', 'PROMPT_VALIDATED', 'DONE', 'STEP_DONE'].includes(dataModelShred.status) :
        false;

    // NEW: Check if model is validated
    const isModelValidated = dataModelShred ? dataModelShred.modelValidated === true : false;

    const isAnyShredGenerating = shredsArr.some(shred => shred.status === 'GENERATING_SPECS') || isWaitingForAPI;

    // Centralized tour state management
    const {
        currentStep,
        isVisible: isTourVisible,
        handleCloseTour,
        handleToggleTour,
        markStepCompleted
    } = useTourState(shredsArr, dataModelShred, featureShreds, initialLoading, loading);

    // The final tour visibility respects both global Guide state and local tour state
    const shouldShowTour = isGlobalGuideActive && isTourVisible;

    // Enhanced wrapper function to handle step completion
    const onGenerateSingleShredWrapper = async (shredId: string) => {
        console.log('Starting spec generation for shred:', shredId);

        // Find the shred to check if it's a model shred
        const shred = shredsArr.find(s => s.shredId === shredId);

        // If it's not a model shred, check if model is validated
        if (!shred?.model && dataModelShred && !isModelValidated) {
            setError("Please validate the Data Model first by clicking 'Explore' and completing the validation process.");
            return;
        }

        // Mark current step as completed when user takes action
        if (currentStep === 'GENERATE_MODEL_SPECS' || currentStep === 'GENERATE_FEATURE_SPECS') {
            markStepCompleted(currentStep);
        }

        await onGenerateSingleShred(shredId);
    };

    // Enhanced wrapper for shred generation
    const onGenerateShredWrapper = async () => {
        console.log('Starting shred generation');

        // Mark generate shreds step as completed
        if (currentStep === 'GENERATE_SHREDS') {
            markStepCompleted(currentStep);
        }

        await onGenerateShred();
    };

    // Enhanced wrapper for explore action
    const onExploreShred = (shredId: string) => {
        console.log('Exploring shred:', shredId);

        // Mark explore steps as completed
        if (currentStep === 'EXPLORE_MODEL' || currentStep === 'EXPLORE_FEATURE') {
            markStepCompleted(currentStep);
        }

        navigate(`/project/${id}/service/${serviceId}/shred/${shredId}`);
    };

    // Event handlers
    const onDeleteShred = async (shredId: string) => {
        try {
            const userConfirmed = window.confirm("Are you sure you want to delete this shred?");
            if (!userConfirmed) return;

            const response = await apiDeleteShred(shredId);
            if (response.data && response.data.status === 200) {
                setShreds((prev) => prev.filter((shred) => shred.shredId !== shredId));
            }
        } catch (errors: any) {
            console.error("Error deleting shred:", errors);
        }
    };

    if (!id || !serviceId) return null;

    // Show loading state while initial data is being fetched
    if (initialLoading) {
        return (
            <DashboardLayout>
                <div className="max-w-7xl mx-auto h-full gap-4 md:gap-6 w-full p-2 sm:p-4 xl:p-6 grow">
                    <ServiceHeader serviceInfo={serviceInfo}/>
                    <WorkflowGuide/>

                    {/* Loading state */}
                    <div className="flex justify-center items-center min-h-[400px]">
                        <div className="text-center">
                            <div
                                className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-gray-600">Loading shreds...</p>
                        </div>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    // Show only the AI generation card if no shreds exist
    if (shredsArr.length === 0) {
        return (
            <DashboardLayout>
                <div className="max-w-7xl mx-auto h-full gap-4 md:gap-6 w-full p-2 sm:p-4 xl:p-6 grow">
                    <ServiceHeader serviceInfo={serviceInfo}/>
                    <WorkflowGuide/>

                    {error && (
                        <Alert showIcon className="mb-8" title={error} type="danger"/>
                    )}

                    <div className="flex justify-center">
                        <div className="max-w-2xl w-full">
                            {loading ? (
                                <ContainerOverlay type="shreds-list">
                                    <AIGenerationCard
                                        id={id}
                                        serviceId={serviceId}
                                        loading={loading}
                                        serviceStatus={serviceStatus}
                                        generateShredModel={generateShredModel}
                                        setGenerateShredModel={setGenerateShredModel}
                                        onGenerateShred={onGenerateShredWrapper}
                                        hasExistingShreds={shredsArr.length > 0}
                                        hasDataModel={!!dataModelShred}
                                        onRefresh={loadShreds}
                                    />
                                </ContainerOverlay>
                            ) : (
                                <AIGenerationCard
                                    id={id}
                                    serviceId={serviceId}
                                    loading={loading}
                                    serviceStatus={serviceStatus}
                                    generateShredModel={generateShredModel}
                                    setGenerateShredModel={setGenerateShredModel}
                                    onGenerateShred={onGenerateShredWrapper}
                                    hasExistingShreds={shredsArr.length > 0}
                                    hasDataModel={!!dataModelShred}
                                    onRefresh={loadShreds}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Centralized Guided Tour */}
                <GuidedShredsTour
                    isActive={shouldShowTour}
                    currentStep={currentStep}
                    shredsArr={shredsArr}
                    dataModelShred={dataModelShred}
                    featureShreds={featureShreds}
                    onClose={handleCloseTour}
                />
            </DashboardLayout>
        );
    }

    // Show full workflow when shreds exist
    return (
        <DashboardLayout>
            <div className="max-w-7xl mx-auto h-full gap-6 w-full p-4 xl:p-6 grow">
                <ServiceHeader serviceInfo={serviceInfo}/>
                <WorkflowGuide/>

                {error && (
                    <Alert showIcon className="mb-8" title={error} type="danger"/>
                )}

                {/* Tour Toggle Button */}
                <div className="flex flex-col sm:flex-row justify-end mb-4 items-center gap-3 sm:gap-4">
                    {!isGlobalGuideActive && (
                        <div className="text-xs sm:text-sm text-gray-600 bg-yellow-50 px-2 sm:px-3 py-1 sm:py-2 rounded-lg border border-yellow-200 text-center sm:text-left">
                            <span>Confused about what to do? </span>
                            <span className="font-medium text-blue-600">Activate <b>GUIDE</b> in header</span>
                        </div>
                    )}
                    <button
                        onClick={handleToggleTour}
                        disabled={!isGlobalGuideActive}
                        className={`inline-flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                            !isGlobalGuideActive
                                ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                : !isTourVisible
                                    ? 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                                    : 'bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100'
                        }`}
                    >
                        <HelpCircle className="w-4 h-4"/>
                        {!isTourVisible ? (
                            <>
                                <Eye className="w-4 h-4"/>
                                <span>Show Guided Tour</span>
                            </>
                        ) : (
                            <>
                                <EyeOff className="w-4 h-4"/>
                                <span>Hide Guided Tour</span>
                            </>
                        )}
                    </button>
                </div>

                {/* Modern Step-by-Step Workflow */}
                <div className="space-y-4 md:space-y-6">
                    {/* STEP 1: Data Model Foundation */}
                    <DataModelStep
                        dataModelShred={dataModelShred}
                        generatingShredId={generatingShredId}
                        isAnyShredGenerating={isAnyShredGenerating}
                        isModelReady={isModelReady}
                        isModelValidated={isModelValidated}
                        projectId={id}
                        onGenerateSpecs={onGenerateSingleShredWrapper}
                        onExplore={onExploreShred}
                        onDelete={onDeleteShred}
                    />

                    {dataModelShred && (
                        <div className="flex justify-center">
                            <div className="flex flex-col items-center">
                                <ArrowDown className="w-8 h-8 text-gray-400 animate-bounce"/>
                                <span className="text-sm text-gray-500 mt-2">Next Step</span>
                            </div>
                        </div>
                    )}

                    {/* STEP 2: Feature Development */}
                    <FeatureDevelopmentStep
                        dataModelShred={dataModelShred}
                        featureShreds={featureShreds}
                        incompleteShreds={incompleteShreds}
                        completeShreds={completeShreds}
                        generatingShredId={generatingShredId}
                        isAnyShredGenerating={isAnyShredGenerating}
                        isModelReady={isModelReady}
                        isModelValidated={isModelValidated}
                        loading={loading}
                        serviceStatus={serviceStatus}
                        generateShredModel={generateShredModel}
                        setGenerateShredModel={setGenerateShredModel}
                        projectId={id}
                        serviceId={serviceId}
                        onGenerateSpecs={onGenerateSingleShredWrapper}
                        onExplore={onExploreShred}
                        onDelete={onDeleteShred}
                        onGenerateShred={onGenerateShredWrapper}
                    />

                    {/* Arrow Connector to Assembly */}
                    {shredsArr.length > 0 && (
                        <div className="flex justify-center">
                            <div className="flex flex-col items-center">
                                <ArrowDown className="w-8 h-8 text-gray-400 animate-bounce"/>
                                <span className="text-sm text-gray-500 mt-2">Ready to Assemble</span>
                            </div>
                        </div>
                    )}

                    {/* STEP 3: Assembly Section */}
                    {shredsArr.length > 0 && (
                        <ServiceAssemblyStep shredsArr={shredsArr}/>
                    )}
                </div>
            </div>

            {/* Centralized Guided Tour */}
            <GuidedShredsTour
                isActive={shouldShowTour}
                currentStep={currentStep}
                shredsArr={shredsArr}
                dataModelShred={dataModelShred}
                featureShreds={featureShreds}
                onClose={handleCloseTour}
            />

        </DashboardLayout>
    );
};

export default Shreds;